<template>
  <div class="pricing-calculator">
    <!-- Read-only summary (submitted orders) -->
    <div v-if="readonlySummary" class="calc-summary">
      <div v-if="projectNameValue" class="summary-project-name">
        <span class="icon has-text-primary">
          <font-awesome-icon :icon="['fal', 'clapperboard']" />
        </span>
        <span>{{ projectNameValue }}</span>
      </div>
      <div class="summary-line-items">
        <div
          v-for="(item, index) in displayLineItems"
          :key="index"
          class="summary-line"
        >
          <span class="line-label">{{ item.label }}</span>
          <span class="line-amount">{{ formatAmount(item.amount) }}</span>
        </div>
      </div>
      <div class="summary-total">
        <span>Total</span>
        <span class="total-amount">{{ formatAmount(displayTotal) }}</span>
      </div>
    </div>

    <!-- Interactive calculator -->
    <div v-else class="calc-blocks">
      <div
        v-for="block in visibleBlocks"
        :key="block.id"
        class="calc-block"
      >
        <!-- Text block -->
        <div v-if="block.type === 'text'" class="field">
          <label class="label">{{ block.label }}</label>
          <p v-if="block.sublabel" class="block-sublabel">{{ block.sublabel }}</p>
          <div class="control">
            <input
              type="text"
              class="input"
              :value="values[block.key] || ''"
              :placeholder="block.placeholder || ''"
              :disabled="disabled"
              @input="updateValue(block.key, $event.target.value)"
            />
          </div>
        </div>

        <!-- Stepper block -->
        <div
          v-else-if="block.type === 'stepper'"
          class="field"
          :class="{ 'is-block-locked': steppersCleared }"
        >
          <div class="stepper-row">
            <div class="stepper-text">
              <span class="stepper-label">{{ block.label }}</span>
              <span v-if="block.sublabel" class="stepper-sublabel">{{ block.sublabel }}</span>
            </div>
            <div class="stepper-controls">
              <button
                type="button"
                class="button"
                :disabled="disabled || steppersCleared || stepperValue(block) <= (block.min ?? 0)"
                :aria-label="`fewer ${block.label}`"
                @click="updateValue(block.key, stepperValue(block) - 1)"
              >
                <span class="icon">
                  <font-awesome-icon :icon="['fal', 'minus']" />
                </span>
              </button>
              <span class="stepper-count">{{ stepperValue(block) }}</span>
              <button
                type="button"
                class="button"
                :disabled="disabled || steppersCleared"
                :aria-label="`more ${block.label}`"
                @click="updateValue(block.key, stepperValue(block) + 1)"
              >
                <span class="icon">
                  <font-awesome-icon :icon="['fal', 'plus']" />
                </span>
              </button>
            </div>
          </div>
        </div>

        <!-- Toggle block (e.g. "I just want the footage") -->
        <div v-else-if="block.type === 'toggle'" class="field">
          <label class="checkbox toggle-row">
            <input
              type="checkbox"
              :checked="!!values[block.key]"
              :disabled="disabled"
              @change="updateValue(block.key, $event.target.checked)"
            />
            <span class="toggle-text">
              <span class="toggle-label">{{ block.label }}</span>
              <span v-if="block.sublabel" class="toggle-sublabel">{{ block.sublabel }}</span>
            </span>
          </label>
        </div>

        <!-- Choice block -->
        <div
          v-else-if="block.type === 'choice'"
          class="field"
          :class="{ 'is-block-locked': isBlockLocked(block) }"
        >
          <label class="label">{{ block.label }}</label>
          <p v-if="block.sublabel" class="block-sublabel">{{ block.sublabel }}</p>

          <!-- Progressive gating: why this block is locked -->
          <p v-if="blockLockReason(block)" class="block-locked-hint">
            <span class="icon">
              <font-awesome-icon :icon="['fal', 'lock']" />
            </span>
            <span>{{ blockLockReason(block) }}</span>
          </p>

          <!-- Repeatable choice (e.g., additional production days) -->
          <template v-if="block.repeatable">
            <div
              v-for="(entry, entryIndex) in choiceEntries(block)"
              :key="entryIndex"
              class="repeat-row"
            >
              <span class="repeat-label">{{ block.itemLabel || 'Day' }} {{ entryIndex + 1 }}</span>
              <div class="choice-options repeat-options">
                <button
                  v-for="option in block.options || []"
                  :key="option.id"
                  type="button"
                  class="button choice-option"
                  :class="{ 'is-selected': entry === option.id }"
                  :disabled="disabled || isBlockLocked(block)"
                  @click="updateRepeatableEntry(block.key, entryIndex, option.id)"
                >
                  <span class="choice-label">{{ option.label }}</span>
                  <span v-if="option.sublabel" class="choice-sublabel">{{ option.sublabel }}</span>
                </button>
              </div>
              <span v-if="block.showDayRate" class="repeat-rate">
                {{ formatAmount(entryDayRate(entry)) }}
              </span>
              <button
                v-if="!disabled && !isBlockLocked(block) && choiceEntries(block).length > 1"
                type="button"
                class="button repeat-remove"
                :aria-label="`Remove ${block.itemLabel || 'day'} ${entryIndex + 1}`"
                @click="removeRepeatableEntry(block.key, entryIndex)"
              >
                <span class="icon">
                  <font-awesome-icon :icon="['fal', 'times']" />
                </span>
              </button>
            </div>
            <button
              v-if="!disabled && !isBlockLocked(block)"
              type="button"
              class="button is-small repeat-add"
              @click="addRepeatableEntry(block)"
            >
              <span class="icon is-small">
                <font-awesome-icon :icon="['fal', 'plus']" />
              </span>
              <span>{{ block.addLabel || 'Add another' }}</span>
            </button>
          </template>

          <!-- Single choice -->
          <div v-else class="choice-options">
            <button
              v-for="option in block.options || []"
              :key="option.id"
              type="button"
              class="button choice-option"
              :class="{ 'is-selected': values[block.key] === option.id }"
              :disabled="disabled || isBlockLocked(block)"
              @click="updateValue(block.key, option.id)"
            >
              <span class="choice-label">{{ option.label }}</span>
              <span v-if="option.sublabel" class="choice-sublabel">{{ option.sublabel }}</span>
              <span v-if="option.spec" class="choice-spec">{{ option.spec }}</span>
            </button>
          </div>

          <!-- Full price breakdown (under the duration block) so the running
               total is explainable: each production day plus priced add-ons -->
          <div v-if="block.showDayRate && durationAnswered" class="day-rate-row">
            <div
              v-for="(item, i) in liveTotals.lineItems"
              :key="i"
              class="breakdown-line"
            >
              <span class="day-rate-label">{{ item.label }}</span>
              <span class="day-rate-amount">{{ formatAmount(item.amount) }}</span>
            </div>
            <div class="breakdown-line breakdown-total">
              <span class="day-rate-label">Total</span>
              <span class="day-rate-amount">{{ formatAmount(liveTotals.total) }}</span>
            </div>
          </div>

          <!-- Conditional hint -->
          <p v-if="hintVisible(block)" class="calc-hint">
            <span class="icon">
              <font-awesome-icon :icon="['fal', 'circle-info']" />
            </span>
            <span>{{ block.hint.text }}</span>
          </p>
        </div>
      </div>

      <!-- Live total — hidden until at least one day has hours selected, so a
           price doesn't appear from stepper costs alone before scheduling -->
      <div v-if="durationAnswered && !hideTotal" class="calc-total">
        <span class="total-caption">Your total — known upfront, no surprises</span>
        <span class="total-amount">{{ formatAmount(liveTotals.total) }}</span>
      </div>

      <!-- Downloadable branded quote PDF (cover + pricing breakdown) -->
      <button
        v-if="showPdfDownload && durationAnswered && liveTotals.total > 0"
        type="button"
        class="button is-text calc-download"
        :class="{ 'is-loading': isGeneratingPdf }"
        :disabled="isGeneratingPdf"
        @click="downloadPdf"
      >
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'arrow-down-to-line']" />
        </span>
        <span>Download proposal (PDF)</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import {
  computeCalculatorTotals,
  calcDayRate,
  calcDayRateForEntry,
  normalizeChoiceEntries,
  isHintVisible,
  formatCalculatorAmount,
  initCalculatorSelections,
  applyCalculatorGating,
  getToggleEffects,
  buildQuotePdfLineItems
} from '@/services/pricingCalculatorService'
import { generateQuotePdf } from '@/services/quotePdfService'
import { useToast } from '@/composables/useToast'
import { logger } from '@/services/loggerService'

const props = defineProps({
  calculator: {
    type: Object,
    required: true
  },
  modelValue: {
    type: Object,
    default: () => ({})
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonlySummary: {
    type: Boolean,
    default: false
  },
  lineItems: {
    type: Array,
    default: () => []
  },
  total: {
    type: Number,
    default: 0
  },
  // Block keys to hide from the interactive view (still priced — display only).
  // Used by the order edit flow to hide the production-day block, which is
  // managed in the wizard's Schedule step instead.
  hiddenBlockKeys: {
    type: Array,
    default: () => []
  },
  // Show a "Download estimate (PDF)" button under the live total. Off by
  // default — only the client MSA order wizard offers the branded quote PDF.
  showPdfDownload: {
    type: Boolean,
    default: false
  },
  // Hide the component's own live-total panel. The MSA wizard renders the total
  // in its modal footer instead, so it suppresses the inline one.
  hideTotal: {
    type: Boolean,
    default: false
  },
  // Order draft creation timestamp (Firestore Timestamp / ISO / Date). Drives
  // the "valid for 30 days" line on the PDF; falls back to now when absent.
  quoteCreatedAt: {
    type: [Object, String, Number, Date],
    default: null
  },
  // Company name, shown as a sub-line under the project name on the quote PDF.
  companyName: {
    type: String,
    default: ''
  },
  // Project name for the quote PDF title. Needed when the name lives outside the
  // calculator (e.g. the MSA wizard's own field); falls back to a calculator
  // text block when not provided.
  projectName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'update:totals', 'repeatable-change'])

const toast = useToast()

// Computed
const blocks = computed(() => props.calculator?.blocks || [])
const visibleBlocks = computed(() =>
  blocks.value.filter((b) => !props.hiddenBlockKeys.includes(b.key))
)

// Merge config defaults under incoming values so the calculator always
// renders complete selections (wizard/preview may pass partial or no values).
// The gating cascade clears selections behind a lock (e.g. a style default
// with zero items) so display, totals, and lock state stay consistent.
const values = computed(() => applyCalculatorGating(props.calculator, {
  ...initCalculatorSelections(props.calculator),
  ...(props.modelValue || {})
}))

const liveTotals = computed(() =>
  computeCalculatorTotals(props.calculator, values.value)
)

const currentDayRate = computed(() =>
  calcDayRate(props.calculator?.dayRateMatrix, values.value)
)

// Summary mode: prefer persisted line items/total, fall back to recompute
const displayLineItems = computed(() =>
  props.lineItems?.length ? props.lineItems : liveTotals.value.lineItems
)

const displayTotal = computed(() =>
  props.lineItems?.length ? props.total : liveTotals.value.total
)

const projectNameValue = computed(() => {
  const textBlock = blocks.value.find(b => b.type === 'text')
  return textBlock ? values.value[textBlock.key] : ''
})

// ───── Progressive gating ─────
// The day-rate matrix encodes the dependency chain: deliverable steppers
// unlock the style block, an answered style unlocks the duration block.
const styleBlockKey = computed(() => props.calculator?.dayRateMatrix?.styleBlockKey || null)
const durationBlockKey = computed(() => props.calculator?.dayRateMatrix?.durationBlockKey || null)

// Effects of any checked toggle block (e.g. "I just want the footage"), both
// configured per block in the calculator editor
const toggleEffects = computed(() => getToggleEffects(props.calculator, values.value))
const steppersCleared = computed(() => toggleEffects.value.clearsSteppers)

const hasStepperItems = computed(() =>
  toggleEffects.value.unlocksProduction ||
  blocks.value.some(b => b.type === 'stepper' && Number(values.value[b.key]) > 0)
)

const isChoiceAnswered = (key) =>
  normalizeChoiceEntries(values.value[key]).some(e => e !== null && e !== undefined && e !== '')

// Reveal the total only once a production day has priced hours (a non-zero day
// rate) — otherwise priced steppers alone would surface a price prematurely.
// Gating on the day rate itself (not "is the choice answered") keeps the total's
// visibility in lockstep with the pricing that actually applies. Configs with no
// day-rate concept (stepper-only) always show their total.
const durationAnswered = computed(() =>
  durationBlockKey.value ? currentDayRate.value > 0 : true
)

const blockLockReason = (block) => {
  if (block.type !== 'choice') return null
  if (block.key === styleBlockKey.value && !hasStepperItems.value) {
    return 'Add at least one item above first'
  }
  if (block.key === durationBlockKey.value) {
    if (!hasStepperItems.value) return 'Add at least one item above first'
    if (!isChoiceAnswered(styleBlockKey.value)) return 'Choose a style first'
  }
  return null
}

const isBlockLocked = (block) => !!blockLockReason(block)

// Methods
const stepperValue = (block) => {
  const value = Number(values.value[block.key])
  return Number.isFinite(value) ? value : (block.min ?? 0)
}

const hintVisible = (block) => isHintVisible(block.hint, values.value)

// Repeatable choice helpers (e.g., additional production days)
const choiceEntries = (block) => normalizeChoiceEntries(values.value[block.key])

const entryDayRate = (entry) => {
  const matrix = props.calculator?.dayRateMatrix
  const styleValue = normalizeChoiceEntries(values.value[matrix?.styleBlockKey])[0]
  return calcDayRateForEntry(matrix, styleValue, entry)
}

const updateRepeatableEntry = (key, index, optionId) => {
  const entries = [...normalizeChoiceEntries(values.value[key])]
  entries[index] = optionId
  updateValue(key, entries)
  // Granular signal so consumers can map a row change to its backing record
  // (e.g. the order editor keeps each production day linked to its event)
  emit('repeatable-change', { key, action: 'set', index, value: optionId })
}

const addRepeatableEntry = (block) => {
  const entries = [...normalizeChoiceEntries(values.value[block.key])]
  // New entries start unanswered unless the block has an explicit default
  entries.push(block.default ?? null)
  updateValue(block.key, entries)
  emit('repeatable-change', { key: block.key, action: 'add', index: entries.length - 1 })
}

const removeRepeatableEntry = (key, index) => {
  const entries = [...normalizeChoiceEntries(values.value[key])]
  if (entries.length <= 1) return
  entries.splice(index, 1)
  updateValue(key, entries)
  emit('repeatable-change', { key, action: 'remove', index })
}

const formatAmount = (amount) =>
  formatCalculatorAmount(amount, props.calculator?.currency)

// Branded quote PDF (cover + pricing breakdown) — delegates to quotePdfService
const isGeneratingPdf = ref(false)

const downloadPdf = async () => {
  if (isGeneratingPdf.value) return
  isGeneratingPdf.value = true
  try {
    await generateQuotePdf({
      projectName: props.projectName || projectNameValue.value,
      companyName: props.companyName,
      // Enriched title + explanatory detail from the calculator's own copy,
      // recomputed from config + current selections (not the terse stored items)
      lineItems: buildQuotePdfLineItems(props.calculator, values.value),
      total: displayTotal.value,
      currency: props.calculator?.currency || 'USD',
      createdAt: props.quoteCreatedAt
    })
  } catch (error) {
    logger.error('[PricingCalculatorSection] Quote PDF download failed', { error: error.message })
    toast.error('Failed to download the proposal. Please try again.')
  } finally {
    isGeneratingPdf.value = false
  }
}

const updateValue = (key, value) => {
  const updated = applyCalculatorGating(props.calculator, { ...values.value, [key]: value })
  emit('update:modelValue', updated)

  // Emit the recomputed totals so the parent can persist the snapshot
  const totals = computeCalculatorTotals(props.calculator, updated)
  emit('update:totals', {
    lineItems: totals.lineItems,
    total: totals.total,
    currency: totals.currency,
    computedAt: new Date().toISOString()
  })
}
</script>

<style lang="scss" scoped>
.pricing-calculator {
  .calc-block {
    padding: 1rem 0;
    border-bottom: 1px solid var(--bulma-border-weak);

    &:first-child {
      padding-top: 0;
    }

    &:last-of-type {
      border-bottom: none;
    }

    .field {
      margin-bottom: 0;
    }

    .label {
      font-size: 0.9rem;
    }
  }

  .block-sublabel {
    font-size: 0.8rem;
    color: var(--bulma-text-weak);
    margin: -0.25rem 0 0.5rem;
  }

  // Progressive gating: locked blocks are subdued with a lock hint
  .field.is-block-locked {
    .label,
    .choice-options,
    .repeat-row,
    .stepper-row {
      opacity: 0.45;
    }
  }

  .block-locked-hint {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.8rem;
    color: var(--bulma-text-weak);
    margin: -0.25rem 0 0.5rem;

    .icon {
      font-size: 0.75rem;
    }
  }

  // Stepper
  .stepper-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    .stepper-text {
      display: flex;
      flex-direction: column;

      .stepper-label {
        font-size: 0.9rem;
        font-weight: 600;
        color: var(--bulma-text-strong);
      }

      .stepper-sublabel {
        font-size: 0.8rem;
        color: var(--bulma-text-weak);
      }
    }

    .stepper-controls {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-shrink: 0;

      .button {
        width: 2.25rem;
        height: 2.25rem;
        padding: 0;
      }

      .stepper-count {
        min-width: 1.5rem;
        text-align: center;
        font-weight: 600;
      }
    }
  }

  // Toggle (checkbox) block
  .toggle-row {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;

    input[type='checkbox'] {
      margin-top: 0.2rem;
      accent-color: var(--bulma-primary);
    }

    .toggle-text {
      display: flex;
      flex-direction: column;
    }

    .toggle-label {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--bulma-text-strong);
    }

    .toggle-sublabel {
      font-size: 0.8rem;
      color: var(--bulma-text-weak);
    }
  }

  // Choice option cards
  .choice-options {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    .choice-option {
      flex: 1;
      min-width: 8rem;
      height: auto;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
      padding: 0.75rem 0.875rem;
      white-space: normal;

      .choice-label {
        font-weight: 600;
        font-size: 0.9rem;
      }

      .choice-sublabel {
        font-size: 0.75rem;
        font-weight: 400;
      }

      .choice-spec {
        font-size: 0.7rem;
        opacity: 0.65;
        font-weight: 400;
      }

      // Selected = light tint of the company brand color. The global .button
      // theme (main.scss) paints fill/border/text from --bulma-button-h/s/l
      // with !important, so feed the brand hue into those vars instead of
      // overriding the painted properties directly.
      &.is-selected {
        --bulma-button-h: var(--bulma-primary-h);
        --bulma-button-s: var(--bulma-primary-s);
        --bulma-button-background-l: var(--bulma-primary-l);
        border-width: 2px !important;
      }
    }
  }

  // Repeatable choice rows (additional production days)
  .repeat-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;

    .repeat-label {
      flex: 0 0 3.5rem;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--bulma-text-weak);
      white-space: nowrap;
    }

    .repeat-options {
      flex: 1;

      .choice-option {
        min-width: 5rem;
        padding: 0.5rem 0.75rem;
      }
    }

    .repeat-rate {
      flex: 0 0 4.5rem;
      text-align: right;
      font-size: 0.875rem;
      font-weight: 600;
      white-space: nowrap;
    }

    .repeat-remove {
      width: 2rem;
      height: 2rem;
      padding: 0;
      border: none;
      background: transparent;
      color: var(--bulma-text-weak);

      &:hover {
        color: var(--bulma-danger);
      }
    }
  }

  .repeat-add {
    margin-top: 0.25rem;
  }

  .day-rate-row {
    margin-top: 0.75rem;

    .breakdown-line {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.25rem 0;

      &.breakdown-total {
        margin-top: 0.25rem;
        padding-top: 0.5rem;
        border-top: 1px solid var(--bulma-border-weak);

        .day-rate-label,
        .day-rate-amount {
          font-weight: 700;
          color: var(--bulma-text-strong);
        }
      }
    }

    .day-rate-label {
      font-size: 0.8rem;
      color: var(--bulma-text-weak);
    }

    .day-rate-amount {
      font-weight: 600;
      white-space: nowrap;
    }
  }

  .calc-hint {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    font-size: 0.8rem;
    color: var(--bulma-text-weak);
    margin-top: 0.5rem;

    .icon {
      font-size: 0.8rem;
      margin-top: 0.1rem;
    }
  }

  // Total panel
  .calc-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    background-color: var(--bulma-scheme-main-bis);
    border-radius: 8px;
    padding: 0.875rem 1rem;
    margin-top: 1.25rem;

    .total-caption {
      font-size: 0.8rem;
      color: var(--bulma-text-weak);
    }

    .total-amount {
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--bulma-text-strong);
    }
  }

  // Quote PDF download — subtle text button beneath the total
  .calc-download {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.75rem;
    padding-left: 0;
    color: var(--bulma-text-weak);
    font-size: 0.85rem;

    &:hover:not(:disabled) {
      color: var(--bulma-text-strong);
    }
  }

  // Read-only summary
  .calc-summary {
    .summary-project-name {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      font-size: 1rem;
      margin-bottom: 0.75rem;
    }

    .summary-line-items {
      .summary-line {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        padding: 0.375rem 0;
        font-size: 0.9rem;
        border-bottom: 1px solid var(--bulma-border-weak);

        .line-label {
          color: var(--bulma-text);
        }

        .line-amount {
          font-weight: 600;
        }
      }
    }

    .summary-total {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.75rem 0 0;
      font-weight: 600;

      .total-amount {
        font-size: 1.25rem;
        color: var(--bulma-text-strong);
      }
    }
  }
}
</style>
